import React from "react";
import ReportCount from "../reports/ReportCount";
import updateAOV from "../../utils/updateAOV";
import updateConversionRate from "../../utils/updateConversionRate";
import Tooltip from "../shared/Tooltip";

const trafficPercent = (cta) =>
  cta.percentage == null || cta.percentage === "" ? 100 : cta.percentage;

const campaignType = (cta) =>
  cta.cta_type === "inline" ? "embed" : cta.cta_type;

const emptyMetric = (
  <p className="text-slate-500 variant-stat" style={{ marginTop: "10px" }}>
    -
  </p>
);

const BroadcastsCampaignsTable = ({
  broadcast,
  range_start,
  range_end,
  currency,
  goals,
}) => {
  const ctas = broadcast.ctas || [];
  if (ctas.length === 0) return null;

  const websiteId = broadcast.website_id;
  const path = `/websites/${websiteId}/events_reports`;
  const rotateCampaigns = broadcast.delivery_mode === "rotate";
  const allocated = ctas.reduce(
    (sum, cta) => sum + Number(trafficPercent(cta)),
    0
  );
  const holdoutPercent = Math.max(0, 100 - allocated);
  const showHoldout = rotateCampaigns && holdoutPercent > 0;

  const metricPayload = (cta, reportType, eventType) => ({
    report_type: reportType,
    report: {
      website_id: websiteId,
      event_type: eventType,
      ...(cta ? { contactable_type: "Cta", contactable_id: cta.id } : {}),
      broadcast_id: broadcast.id,
      range_start: range_start,
      range_end: range_end,
    },
  });

  const countCell = (id, cta, eventType) => (
    <div className="col-xs-1" style={{ paddingTop: "25px", minWidth: "100px" }}>
      <p className="text-slate-500 variant-stat" style={{ marginTop: "10px" }}>
        <ReportCount
          id={id}
          inline={true}
          unstyled={true}
          hide_comparison_badge={true}
          path={path}
          payload={metricPayload(cta, "distinct_count", eventType)}
        />
      </p>
    </div>
  );

  const headCell = (label) => (
    <div className="col-xs-1" style={{ minWidth: "100px" }}>
      <span className="row-heading">{label}</span>
    </div>
  );

  const checkoutCells = (cta, eventType, idPrefix) => {
    const ordersId = `${idPrefix}-orders`;
    const revenueId = `${idPrefix}-revenue`;
    const aovId = `${idPrefix}-aov`;

    return (
      <React.Fragment>
        <div
          className="col-xs-1"
          style={{ paddingTop: "25px", minWidth: "100px" }}
        >
          <p
            className="text-slate-500 variant-stat"
            style={{ marginTop: "10px" }}
          >
            <ReportCount
              id={ordersId}
              inline={true}
              unstyled={true}
              hide_comparison_badge={true}
              path={path}
              payload={metricPayload(cta, "count", eventType)}
              callback={() => {
                updateAOV(`#${ordersId}`, `#${revenueId}`, `#${aovId}`);
              }}
            />
          </p>
        </div>
        <div
          className="col-xs-1"
          style={{ paddingTop: "25px", maxWidth: "125px" }}
        >
          <p
            className="text-slate-500 variant-stat"
            style={{ marginTop: "10px" }}
          >
            <ReportCount
              id={revenueId}
              inline={true}
              unstyled={true}
              hide_comparison_badge={true}
              currency={currency}
              path={path}
              payload={metricPayload(cta, "revenue", eventType)}
            />
          </p>
        </div>
        <div
          className="col-xs-1"
          style={{ paddingTop: "25px", maxWidth: "125px" }}
        >
          <p
            className="text-slate-500 variant-stat"
            style={{ marginTop: "10px" }}
          >
            {currency}
            <span id={aovId}>0.00</span>
          </p>
        </div>
      </React.Fragment>
    );
  };

  const holdoutViewsId = `broadcast-${broadcast.id}-holdout-views`;

  return (
    <div className="row hard-center mt-[30px] mb-[100px] pb-[15px] border-solid border-[1px] border-slate-200 rounded-xl shadow-sm">
      <div
        className="col-xs-12 some-vertical-space"
        style={{ borderBottom: "1px solid #f8f8fb" }}
      >
        <h4 className="bold">Campaigns</h4>
        {rotateCampaigns && (
          <p className="text-xl text-slate-500 some-vertical-space">
            Unallocated traffic is holdout. Matching visitors see no campaign.
          </p>
        )}
      </div>
      <div
        className="col-xs-12"
        style={{ overflowX: "auto", marginBottom: "15px" }}
      >
        <div
          className="row hard-center"
          style={{
            position: "sticky",
            top: "5px",
            zIndex: 1,
            minHeight: "36px",
            background: "#fff",
            borderBottom: "1px solid #f8f8fb",
            minWidth: "900px",
            marginTop: "20px",
          }}
        >
          <div className="col-xs-1" style={{ width: "100px" }}>
            <span className="row-heading">Campaign</span>
          </div>
          <div className="col-xs-2 !w-[64px] !px-2">
            <span className="row-heading">Traffic</span>
          </div>
          <div
            className="col-xs-1"
            style={{ width: "180px", maxWidth: "180px" }}
          >
            <span className="row-heading">Name</span>
          </div>
          <div className="col-xs-1" style={{ maxWidth: "125px" }}>
            <span className="row-heading">Viewers</span>
          </div>
          <div className="col-xs-1" style={{ minWidth: "125px" }}>
            <span className="row-heading">Conversions</span>
          </div>
          <div className="col-xs-1" style={{ minWidth: "125px" }}>
            <span className="row-heading">Completions</span>
          </div>
          {goals.checkouts && (
            <React.Fragment>
              <div className="col-xs-1" style={{ minWidth: "100px" }}>
                <span className="row-heading">Orders</span>
              </div>
              <div className="col-xs-1" style={{ maxWidth: "125px" }}>
                <span className="row-heading">Revenue</span>
              </div>
              <div className="col-xs-1" style={{ maxWidth: "125px" }}>
                <span className="row-heading">AOV</span>
              </div>
            </React.Fragment>
          )}
          {goals.bookings && headCell("Bookings")}
          {goals.custom && headCell(goals.customGoalLabel)}
        </div>

        {showHoldout && (
          <div
            className="row hard-center variant-list broadcast-holdout-row"
            style={{ marginTop: "16px" }}
          >
            <div className="col-xs-1" style={{ width: "100px" }}>
              <div
                className="broadcasts-cta-screenshot"
                style={{
                  backgroundImage: "none",
                  backgroundColor: "#f8fafc",
                  border: "1px dashed #cbd5e1",
                }}
              />
            </div>
            <div
              className="col-xs-2 no-padding-right !w-[64px] !px-2"
              style={{ paddingTop: "30px" }}
            >
              <span className="text-slate-500 variant-stat">
                {holdoutPercent}%
              </span>
            </div>
            <div
              className="col-xs-1"
              style={{
                width: "180px",
                maxWidth: "180px",
                paddingTop: "22px",
                overflow: "hidden",
              }}
            >
              <span className="variant-stat">Control</span>
              <Tooltip
                tooltip="Visitors assigned to holdout who matched this experiment. Counted on eligibility rather than display, and orders, bookings and custom goals are credited on assignment rather than engagement, so these are not like-for-like rates against campaign rows."
                inline={true}
              />
              <div className="text-lg text-slate-500">nothing shows</div>
            </div>
            <div
              className="col-xs-1"
              style={{ paddingTop: "25px", maxWidth: "125px" }}
            >
              <p
                className="text-slate-500 variant-stat"
                style={{ marginTop: "10px" }}
              >
                <ReportCount
                  id={holdoutViewsId}
                  inline={true}
                  unstyled={true}
                  hide_comparison_badge={true}
                  path={path}
                  payload={metricPayload(null, "distinct_count", "Holdout")}
                />
              </p>
            </div>
            <div
              className="col-xs-1"
              style={{ paddingTop: "25px", minWidth: "125px" }}
            >
              {emptyMetric}
            </div>
            <div
              className="col-xs-1"
              style={{ paddingTop: "25px", minWidth: "125px" }}
            >
              {emptyMetric}
            </div>
            {goals.checkouts &&
              checkoutCells(
                null,
                "Holdout Order",
                `broadcast-${broadcast.id}-holdout`
              )}
            {goals.bookings &&
              countCell(
                `broadcast-${broadcast.id}-holdout-bookings`,
                null,
                "Holdout Booking"
              )}
            {goals.custom &&
              countCell(
                `broadcast-${broadcast.id}-holdout-custom-goals`,
                null,
                "Holdout Custom Goal"
              )}
          </div>
        )}

        {ctas.map((cta) => {
          const rowId = `broadcast-${broadcast.id}-cta-${cta.id}`;
          const viewsId = `${rowId}-views`;
          const conversionsId = `${rowId}-conversions`;
          const conversionRateId = `${rowId}-conversion-rate`;
          const completionsId = `${rowId}-completions`;
          const completionRateId = `${rowId}-completion-rate`;
          const href = `/websites/${websiteId}/cta/${cta.id}`;
          const campaignName = cta.name ? cta.name : `Campaign ${cta.id}`;

          return (
            <div
              key={cta.id}
              className="row hard-center variant-list"
              style={{ borderTop: "1px solid #f8f8fb" }}
            >
              <div className="col-xs-1" style={{ width: "100px" }}>
                <a href={href}>
                  <div
                    className="broadcasts-cta-screenshot"
                    style={{
                      backgroundImage: cta.screenshot
                        ? `url('${cta.screenshot}')`
                        : undefined,
                    }}
                  />
                </a>
              </div>
              <div
                className="col-xs-2 no-padding-right !w-[64px] !px-2"
                style={{ paddingTop: "30px" }}
              >
                <span className="text-slate-500 variant-stat">
                  {trafficPercent(cta)}%
                </span>
              </div>
              <div
                className="col-xs-1"
                style={{
                  width: "180px",
                  maxWidth: "180px",
                  paddingTop: "22px",
                  overflow: "hidden",
                }}
              >
                <a
                  className="text-black"
                  href={href}
                  title={campaignName}
                  style={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span className="variant-stat">{campaignName}</span>
                </a>
                <div className="text-lg text-slate-500 capitalize">
                  {campaignType(cta)}
                </div>
              </div>
              <div
                className="col-xs-1"
                style={{ paddingTop: "25px", maxWidth: "125px" }}
              >
                <p
                  className="text-slate-500 variant-stat"
                  style={{ marginTop: "10px" }}
                >
                  <ReportCount
                    id={viewsId}
                    inline={true}
                    unstyled={true}
                    hide_comparison_badge={true}
                    path={path}
                    payload={metricPayload(cta, "distinct_count", "View")}
                  />
                </p>
              </div>
              <div
                className="col-xs-1"
                style={{ paddingTop: "25px", minWidth: "125px" }}
              >
                <p
                  className="text-slate-500 variant-stat"
                  style={{ paddingTop: "10px" }}
                >
                  <ReportCount
                    id={conversionsId}
                    inline={true}
                    unstyled={true}
                    hide_comparison_badge={true}
                    path={path}
                    payload={metricPayload(cta, "distinct_count", "Complete")}
                    callback={() => {
                      updateConversionRate(
                        `#${viewsId}`,
                        `#${conversionsId}`,
                        `#${conversionRateId}`
                      );
                    }}
                  />{" "}
                  (<span id={conversionRateId}>0.00%</span>)
                </p>
              </div>
              <div
                className="col-xs-1"
                style={{ paddingTop: "25px", minWidth: "125px" }}
              >
                <p
                  className="text-slate-500 variant-stat"
                  style={{ marginTop: "10px" }}
                >
                  <ReportCount
                    id={completionsId}
                    inline={true}
                    unstyled={true}
                    hide_comparison_badge={true}
                    path={path}
                    payload={metricPayload(cta, "distinct_count", "Completion")}
                    callback={() => {
                      updateConversionRate(
                        `#${viewsId}`,
                        `#${completionsId}`,
                        `#${completionRateId}`
                      );
                    }}
                  />{" "}
                  (<span id={completionRateId}>0.00%</span>)
                </p>
              </div>
              {goals.checkouts && checkoutCells(cta, "Order", rowId)}
              {goals.bookings && countCell(`${rowId}-bookings`, cta, "Booking")}
              {goals.custom &&
                countCell(`${rowId}-custom-goals`, cta, "Custom Goal")}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BroadcastsCampaignsTable;
